import type {
  ProductEditorForm,
  ProductEditorSyncValidator,
  ProductEditorAsyncValidator,
} from '@/components/forms/ProductForm.types'
import type {
  ProductEditorValues,
  ProductEditorCollection,
} from '@/lib/services/products/products.form'
import type { Organization } from '@/lib/services/organizations/organizations.types'
import type { ProductRole } from '@/lib/product-role'
import type { KeyboardEvent, FocusEvent } from 'react'
import { TextareaInputControl } from '@/components/form/TextareaInput'
import { TextInputControl } from '@/components/form/TextInput'
import FieldInfo from '@/components/form/FieldInfo'
import { Button } from '@/components/ui/button'
import { getProductRole } from '@/lib/product-role'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import { slugify } from '@/lib/utils'
import { Pencil } from 'lucide-react'
import { useRef, useState } from 'react'
import { resolveStorefrontPreviewBaseUrl } from '../forms/DesignForm/storefrontPreviewConfig'

type SeoCollection = Partial<
  Pick<ProductEditorCollection, 'name' | 'slug' | 'source_key' | 'source_system'>
>
type SeoValues = Partial<
  Pick<
    ProductEditorValues,
    | 'name'
    | 'description'
    | 'page_title'
    | 'meta_description'
    | 'url_slug'
    | 'product_role'
    | 'is_add_on'
    | 'is_add_on_only'
    | 'product_catalog_type'
  >
> & { collections?: SeoCollection[] }
type SeoOrganization = Partial<
  Pick<Organization, 'name' | 'primary_domain' | 'favicon_url' | 'demo_id'>
>
type SeoFieldName = 'page_title' | 'meta_description' | 'url_slug'

function createEmptySeoPreviewDrafts() {
  return {
    meta_description: '',
    page_title: '',
    url_slug: '',
  }
}

function getSeoPreviewState(value: SeoValues | null | undefined) {
  const name = String(value?.name ?? '').trim()
  const description = String(value?.description ?? '').trim()
  const pageTitle = String(value?.page_title ?? '').trim()
  const metaDescription = String(value?.meta_description ?? '').trim()
  const urlSlug = String(value?.url_slug ?? '').trim()

  return {
    title: pageTitle || name || 'Untitled product',
    description: metaDescription || description || 'Your product description will show here.',
    slug: urlSlug || slugify(name) || 'your-product',
  }
}

function normalizeSeoPreviewBaseUrl(value: string | null | undefined) {
  if (typeof value !== 'string' || value.trim() === '') {
    return null
  }

  try {
    const normalized = new URL(
      value.startsWith('http://') || value.startsWith('https://') ? value : `https://${value}`
    )

    normalized.pathname = ''
    normalized.search = ''
    normalized.hash = ''

    return normalized.toString().replace(/\/$/, '')
  } catch {
    return null
  }
}

function isSubscriptionPackageRole(productRole: ProductRole | undefined) {
  return productRole === 'subscription_package'
}

function getSeoPreviewPath(
  collections: SeoCollection[] | null | undefined,
  slug: string,
  productRole: ProductRole = 'product'
) {
  if (isSubscriptionPackageRole(productRole)) {
    const collectionSlug =
      collections?.find(isSubscriptionPackageCollection)?.slug ?? 'subscriptions'

    return `/collections/${collectionSlug}/${slug}`
  }

  const collectionSlug =
    collections?.find(
      (collection) => typeof collection?.slug === 'string' && collection.slug.trim()
    )?.slug ?? 'all'

  return `/collections/${collectionSlug}/${slug}`
}

function getSeoPreviewUrl({
  organization,
  collections,
  productRole,
  slug,
}: {
  organization?: SeoOrganization | null
  collections?: SeoCollection[]
  productRole?: ProductRole
  slug: string
}) {
  const path = getSeoPreviewPath(collections, slug, productRole)
  const baseUrl =
    normalizeSeoPreviewBaseUrl(organization?.primary_domain) ??
    resolveStorefrontPreviewBaseUrl(organization)

  return {
    path,
    url: baseUrl ? new URL(path, `${baseUrl}/`).toString() : path,
  }
}

function formatSeoPreviewSegment(segment: string | null | undefined) {
  return String(segment ?? '')
    .trim()
    .replace(/[-_]+/g, ' ')
}

function getSeoPreviewDisplayParts({ path, url }: { path: string; url: string }) {
  const pathLabel = path.split('/').filter(Boolean).map(formatSeoPreviewSegment)
  let hostname = ''

  try {
    const parsedUrl = new URL(url)
    hostname = parsedUrl.hostname.replace(/^www\./, '')
  } catch {
    // Ignore invalid preview URLs and fall back to the path-only label.
  }

  const displayParts = [hostname, ...pathLabel].filter(Boolean)

  return {
    displayUrl: displayParts.join(' › '),
    displayUrlPrefix: displayParts.slice(0, -1).join(' › '),
    slugLabel: pathLabel.at(-1) ?? '',
  }
}

function getSearchPreviewSnippet(description: string | null | undefined, maxLength = 160) {
  const normalizedDescription = String(description ?? '').trim()

  if (normalizedDescription.length <= maxLength) {
    return normalizedDescription
  }

  const trimmedDescription = normalizedDescription.slice(0, maxLength).trim()
  const lastSpaceIndex = trimmedDescription.lastIndexOf(' ')
  const clippedDescription =
    lastSpaceIndex >= Math.floor(maxLength * 0.6)
      ? trimmedDescription.slice(0, lastSpaceIndex)
      : trimmedDescription

  return `${clippedDescription}...`
}

function getSeoPreviewPresentation({
  organization,
  values,
  canViewCollections = true,
}: {
  organization?: SeoOrganization | null
  values?: SeoValues | null
  canViewCollections?: boolean
}) {
  const seoPreview = getSeoPreviewState(values)
  const seoUrl = getSeoPreviewUrl({
    organization,
    collections: canViewCollections ? values?.collections : [],
    productRole: values?.product_role,
    slug: seoPreview.slug,
  })
  const faviconUrl =
    typeof organization?.favicon_url === 'string' ? organization.favicon_url.trim() : ''
  let siteName = String(organization?.name ?? '').trim()

  if (!siteName) {
    try {
      siteName = new URL(seoUrl.url).hostname.replace(/^www\./, '')
    } catch {
      siteName = ''
    }
  }

  return {
    ...seoPreview,
    ...seoUrl,
    ...getSeoPreviewDisplayParts(seoUrl),
    faviconUrl: faviconUrl || null,
    snippetDescription: getSearchPreviewSnippet(seoPreview.description),
    siteName: siteName || 'Your flower shop',
  }
}

function buildProductSeoPreviewSource(
  values: SeoValues,
  canViewCollections: boolean,
  subscriptionCollection: SeoCollection | null = null
) {
  const editableCollections = canViewCollections ? (values?.collections ?? []) : []
  const productRole = getProductRole(values)

  return {
    collections:
      isSubscriptionPackageRole(productRole) && subscriptionCollection
        ? [subscriptionCollection, ...editableCollections]
        : editableCollections,
    description: String(values?.description ?? '').trim(),
    meta_description: String(values?.meta_description ?? '').trim(),
    name: String(values?.name ?? '').trim(),
    page_title: String(values?.page_title ?? '').trim(),
    product_role: productRole,
    url_slug: String(values?.url_slug ?? '').trim(),
  }
}

function getSeoFieldDefaultValue(fieldName: SeoFieldName, values: SeoValues) {
  switch (fieldName) {
    case 'page_title':
      return String(values?.name ?? '').trim()
    case 'meta_description':
      return String(values?.description ?? '').trim()
    case 'url_slug':
      return slugify(String(values?.name ?? '').trim())
    default:
      return ''
  }
}

function getSeoFieldSuggestedValue(fieldName: SeoFieldName, values: SeoValues) {
  const rawValue = String(values?.[fieldName] ?? '').trim()

  return rawValue || getSeoFieldDefaultValue(fieldName, values)
}

function normalizeSeoFieldDraft(fieldName: SeoFieldName, value: string | null | undefined) {
  const normalizedValue = String(value ?? '').trim()

  if (fieldName === 'url_slug') {
    return slugify(normalizedValue)
  }

  return normalizedValue
}

function getStoredSeoFieldValue(
  fieldName: SeoFieldName,
  draftValue: string,
  values: SeoValues,
  initialRawValue = ''
) {
  const normalizedDraft = normalizeSeoFieldDraft(fieldName, draftValue)
  const defaultValue = getSeoFieldDefaultValue(fieldName, values)
  const normalizedInitialRawValue = String(initialRawValue ?? '').trim()

  return !normalizedInitialRawValue && normalizedDraft === defaultValue ? '' : normalizedDraft
}

function ProductSeoPreview<
  TOnMount extends ProductEditorSyncValidator,
  TOnChange extends ProductEditorSyncValidator,
  TOnChangeAsync extends ProductEditorAsyncValidator,
  TOnBlur extends ProductEditorSyncValidator,
  TOnBlurAsync extends ProductEditorAsyncValidator,
  TOnSubmit extends ProductEditorSyncValidator,
  TOnSubmitAsync extends ProductEditorAsyncValidator,
  TOnDynamic extends ProductEditorSyncValidator,
  TOnDynamicAsync extends ProductEditorAsyncValidator,
  TOnServer extends ProductEditorAsyncValidator,
  TSubmitMeta,
>({
  form,
  isReadOnly,
  organization,
  canViewCollections,
  includeCollections = true,
  onUrlSlugManualEdit,
  previewSource,
}: {
  form: ProductEditorForm<
    TOnMount,
    TOnChange,
    TOnChangeAsync,
    TOnBlur,
    TOnBlurAsync,
    TOnSubmit,
    TOnSubmitAsync,
    TOnDynamic,
    TOnDynamicAsync,
    TOnServer,
    TSubmitMeta
  >
  isReadOnly?: boolean
  organization?: SeoOrganization | null
  canViewCollections?: boolean
  includeCollections?: boolean
  onUrlSlugManualEdit?: () => void
  previewSource?: SeoValues | null
}) {
  const previewRef = useRef<HTMLDivElement>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [draftValues, setDraftValues] = useState(() => createEmptySeoPreviewDrafts())
  const [initialRawValues, setInitialRawValues] = useState(() => createEmptySeoPreviewDrafts())

  const stopEditing = () => {
    setIsEditing(false)
  }

  const startEditing = (values: SeoValues) => {
    setInitialRawValues({
      meta_description: String(values?.meta_description ?? '').trim(),
      page_title: String(values?.page_title ?? '').trim(),
      url_slug: String(values?.url_slug ?? '').trim(),
    })
    setDraftValues({
      meta_description: getSeoFieldSuggestedValue('meta_description', values),
      page_title: getSeoFieldSuggestedValue('page_title', values),
      url_slug: getSeoFieldSuggestedValue('url_slug', values),
    })
    setIsEditing(true)
  }

  const updateDraftValue = (fieldName: SeoFieldName, nextDraft: string) => {
    setDraftValues((currentDrafts) => ({
      ...currentDrafts,
      [fieldName]: nextDraft,
    }))
  }

  const revertEditing = () => {
    form.setFieldValue('page_title', initialRawValues.page_title)
    form.setFieldValue('url_slug', initialRawValues.url_slug)
    form.setFieldValue('meta_description', initialRawValues.meta_description)

    stopEditing()
  }

  const handleSingleLineKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
      stopEditing()
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      revertEditing()
    }
  }

  const handleTextareaKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      event.currentTarget.blur()
      stopEditing()
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      revertEditing()
    }
  }

  const handleEditingFieldBlur = (
    field: { handleBlur: () => void },
    fieldName: SeoFieldName,
    event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    field.handleBlur()
    updateEditingValue(fieldName, event.target.value, {
      normalizeDraft: true,
    })

    window.setTimeout(() => {
      if (!previewRef.current?.contains(document.activeElement)) {
        stopEditing()
      }
    }, 0)
  }

  const getEditingValues = () => ({
    name: String(form.getFieldValue('name') ?? '').trim(),
    description: String(form.getFieldValue('description') ?? '').trim(),
    page_title: String(form.getFieldValue('page_title') ?? '').trim(),
    meta_description: String(form.getFieldValue('meta_description') ?? '').trim(),
    product_role: getProductRole(form.store.state.values),
    url_slug: String(form.getFieldValue('url_slug') ?? '').trim(),
    collections: includeCollections ? (form.getFieldValue('collections') ?? []) : [],
  })
  const updateEditingValue = (
    fieldName: SeoFieldName,
    nextDraft: string,
    { normalizeDraft = false } = {}
  ) => {
    const nextDisplayValue = normalizeDraft
      ? normalizeSeoFieldDraft(fieldName, nextDraft)
      : nextDraft
    const nextValue = getStoredSeoFieldValue(
      fieldName,
      nextDraft,
      getEditingValues(),
      initialRawValues?.[fieldName]
    )

    updateDraftValue(fieldName, nextDisplayValue)
    form.setFieldValue(fieldName, nextValue)
  }
  const previewValues = {
    name: previewSource?.name ?? '',
    description: previewSource?.description ?? '',
    page_title: previewSource?.page_title ?? '',
    product_role: previewSource?.product_role ?? 'product',
    meta_description: previewSource?.meta_description ?? '',
    url_slug: previewSource?.url_slug ?? '',
    collections: includeCollections ? (previewSource?.collections ?? []) : [],
  }
  const preview = getSeoPreviewPresentation({
    canViewCollections,
    organization,
    values: previewValues,
  })

  return (
    <div
      ref={previewRef}
      className="relative min-w-0 overflow-hidden rounded-xl border border-border bg-card px-5 py-5"
    >
      {!isReadOnly ? (
        <Button
          type="button"
          variant="ghostMuted"
          size="icon-xs"
          aria-label={isEditing ? 'Done editing search preview' : 'Edit search preview'}
          onClick={() => (isEditing ? stopEditing() : startEditing(getEditingValues()))}
          className="absolute top-4 right-4"
        >
          <Pencil className="size-4" />
        </Button>
      ) : null}

      <div className="flex items-start gap-3 pr-10">
        {preview.faviconUrl ? (
          <img
            src={preview.faviconUrl}
            alt={`${preview.siteName} favicon`}
            className="mt-0.5 size-9 shrink-0 rounded-full border border-border bg-card object-contain p-1"
          />
        ) : (
          <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full border border-border/60 bg-muted/40 text-xs font-semibold uppercase text-foreground">
            {String(preview?.siteName ?? 'Y')
              .trim()
              .charAt(0)
              .toUpperCase()}
          </div>
        )}
        <div className="min-w-0 space-y-0.5">
          <p className="truncate text-sm font-medium text-foreground">{preview.siteName}</p>
          {isEditing ? (
            <form.AppField name="url_slug">
              {(field) => (
                <div className="flex min-w-0 flex-wrap items-center gap-x-1 gap-y-2 text-xs text-muted-foreground">
                  {preview.displayUrlPrefix ? (
                    <span className="truncate">{preview.displayUrlPrefix} ›</span>
                  ) : null}
                  <TextInputControl
                    fieldName={field.name}
                    fieldValue={draftValues.url_slug}
                    fieldInfo={<FieldInfo />}
                    onFieldChange={(nextDraft) => {
                      onUrlSlugManualEdit?.()
                      updateEditingValue('url_slug', nextDraft)
                    }}
                    id="seo-url-handle"
                    label={null}
                    aria-label="URL Handle"
                    className="min-w-[10rem] flex-1 text-foreground"
                    onBlur={(event) => handleEditingFieldBlur(field, 'url_slug', event)}
                    onKeyDown={handleSingleLineKeyDown}
                  />
                </div>
              )}
            </form.AppField>
          ) : (
            <p className="truncate text-xs text-muted-foreground">{preview.displayUrl}</p>
          )}
        </div>
      </div>

      <div className="mt-4 space-y-1.5">
        {isEditing ? (
          <form.AppField name="page_title">
            {(field) => (
              <TextInputControl
                fieldName={field.name}
                fieldValue={draftValues.page_title}
                fieldInfo={<FieldInfo />}
                onFieldChange={(nextDraft) => updateEditingValue('page_title', nextDraft)}
                id="seo-page-title"
                label={null}
                aria-label="Page Title"
                autoFocus
                onBlur={(event) => handleEditingFieldBlur(field, 'page_title', event)}
                onKeyDown={handleSingleLineKeyDown}
              />
            )}
          </form.AppField>
        ) : (
          <p className="min-w-0 break-words text-xl leading-6 text-[#1a0dab] dark:text-[#8ab4f8]">
            {preview.title}
          </p>
        )}

        {isEditing ? (
          <form.AppField name="meta_description">
            {(field) => (
              <TextareaInputControl
                fieldName={field.name}
                fieldValue={draftValues.meta_description}
                fieldInfo={<FieldInfo />}
                onFieldChange={(nextDraft) => updateEditingValue('meta_description', nextDraft)}
                id="seo-page-description"
                label={null}
                aria-label="Page Description"
                rows={4}
                className="resize-none"
                onBlur={(event) => handleEditingFieldBlur(field, 'meta_description', event)}
                onKeyDown={handleTextareaKeyDown}
              />
            )}
          </form.AppField>
        ) : (
          <p className="min-w-0 break-words text-sm leading-6 text-[#4d5156] dark:text-[#bdc1c6]">
            {preview.snippetDescription ?? preview.description}
          </p>
        )}
      </div>
    </div>
  )
}

export { buildProductSeoPreviewSource, getSeoPreviewPresentation, ProductSeoPreview }
